import { motion } from "framer-motion";
import { useScrollAnimation } from "../../utils/useScrollAnimation";

function FaqSection() {
  const faqItems = [
    {
      question: "How does AI email categorization work?",
      answer: "Our AI analyzes the content, sender, and patterns in your emails to automatically categorize them. It learns from your manual categorizations to become more accurate over time."
    },
    {
      question: "Will MailMaid work with my email provider?",
      answer: "MailMaid integrates with major email providers including Gmail, Outlook, Yahoo Mail, and Apple Mail. We're constantly adding support for additional providers."
    },
    {
      question: "Is my email data secure with MailMaid?",
      answer: "Absolutely. We use bank-level encryption and never store the content of your emails on our servers. Your privacy and security are our top priorities."
    },
    {
      question: "Can I customize the AI to match my preferences?",
      answer: "Yes, MailMaid learns from your interactions and adjusts its recommendations based on your behavior. You can also manually customize rules and preferences."
    },
    {
      question: "What's the difference between free and paid plans?",
      answer: "The free plan includes basic categorization and processing for up to 100 emails monthly. Paid plans offer unlimited email processing, advanced AI features, and priority support."
    },
    {
      question: "How much time will MailMaid save me?",
      answer: "Most users report saving 5+ hours per week. The exact time savings depend on your email volume and current management practices."
    }
  ];

  return (
    <section className="py-20 px-4 bg-[#fff5eb]">
      <div className="container mx-auto max-w-4xl">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl font-bold mb-4 text-[#694e42]">Frequently Asked Questions</h2>
          <p className="text-lg text-[#8a7266] max-w-2xl mx-auto">
            Get answers to the most common questions about MailMaid's features.
          </p>
        </motion.div>
        
        <div className="space-y-6">
          {faqItems.map((faq, index) => {
            const { ref, isInView } = useScrollAnimation();
            
            return (
              <motion.div
                key={index}
                ref={ref}
                className="bg-white rounded-xl p-6 shadow-sm border border-[#e2dad3]"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <details className="group">
                  <summary className="flex justify-between items-center cursor-pointer list-none">
                    <h3 className="font-bold text-lg text-[#694e42]">{faq.question}</h3>
                    <svg
                      className="w-5 h-5 text-[#ff8c42] transition-transform duration-300 group-open:rotate-180"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M19 9l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </summary>
                  <div className="mt-4 text-[#8a7266]">
                    <p>{faq.answer}</p>
                  </div>
                </details>
              </motion.div>
            );
          })}
        </div>
        
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <p className="text-[#8a7266] mb-4">Still have questions?</p>
          <a 
            href="/contact"
            className="text-[#ff8c42] font-medium hover:underline"
          >
            Contact our support team for immediate assistance →
          </a>
        </motion.div>
      </div>
    </section>
  );
}

export default FaqSection;